import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from './ui/button';

// WO-53 — every signed-in account must have accepted the CURRENT Terms of Service
// (BILAL, 30/09/2026: the click-accepted DPA depends on that record). New accounts
// accept at sign-up; accounts created before, or after a change of the Terms, are
// asked here once, inside the app. The server keeps the record (version, date, IP).
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const TEXT = {
  en: {
    title: 'Please accept our Terms of Service',
    // Lucas 30/09/2026: users are told about the new privacy policy HERE, not by e-mail.
    notice: 'We updated our Privacy Policy on 1 October 2026. It now names the providers we use and where they are, says how long we keep each kind of data, and explains our AI features and chat assistants.',
    body: 'To keep using Mid2Bid, please read and accept our Terms of Service. Our Privacy Policy explains how we handle your data.',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',
    accept: 'I accept the Terms of Service',
    saving: 'Saving…',
    signOut: 'Sign out',
    error: 'Your acceptance could not be saved. Please try again.',
  },
  ar: {
    title: 'يُرجى قبول شروط الخدمة',
    notice: 'حدّثنا سياسة الخصوصية في 1 أكتوبر 2026. وهي تذكر الآن مزوّدي الخدمة الذين نستخدمهم وأماكنهم، ومدة احتفاظنا بكل نوع من البيانات، وتشرح ميزات الذكاء الاصطناعي والمساعدين الآليين.',
    body: 'لمواصلة استخدام Mid2Bid، يُرجى قراءة شروط الخدمة وقبولها. وتوضح سياسة الخصوصية كيف نتعامل مع بياناتك.',
    terms: 'شروط الخدمة',
    privacy: 'سياسة الخصوصية',
    accept: 'أقبل شروط الخدمة',
    saving: 'جارٍ الحفظ…',
    signOut: 'تسجيل الخروج',
    error: 'تعذّر حفظ موافقتك. يُرجى المحاولة مرة أخرى.',
  },
};

export default function TermsGate() {
  const { user, token, logout } = useAuth();
  const { isRTL } = useLanguage();
  const location = useLocation();
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const t = isRTL ? TEXT.ar : TEXT.en;
  const inApp = location.pathname.startsWith('/dashboard');

  useEffect(() => {
    let cancelled = false;
    if (!user || !token || !inApp) return undefined;
    fetch(`${API_URL}/api/auth/terms`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled) setStatus(d); })
      .catch(() => { /* network blip: ask again on the next page */ });
    return () => { cancelled = true; };
  }, [user, token, inApp]);

  if (!user || !inApp || !status || !status.needs_acceptance) return null;

  const accept = async () => {
    setSaving(true);
    setError('');
    try {
      const r = await fetch(`${API_URL}/api/auth/accept-terms`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ version: status.current_version }),
      });
      if (!r.ok) throw new Error('not saved');
      setStatus(await r.json());
    } catch (e) {
      setError(t.error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" role="dialog" aria-modal="true" data-testid="terms-gate">
      <div className={`bg-background border border-border rounded-xl max-w-md w-full p-6 space-y-4 ${isRTL ? 'text-right' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <h2 className="text-xl font-semibold">{t.title}</h2>
        <p className="text-sm rounded-md border border-border bg-muted/50 p-3" data-testid="privacy-update-notice">{t.notice}</p>
        <p className="text-sm text-muted-foreground">{t.body}</p>
        <div className={`flex gap-4 text-sm ${isRTL ? 'flex-row-reverse justify-end' : ''}`}>
          <a href={status.terms_url || '/terms-of-service'} target="_blank" rel="noopener noreferrer" className="text-primary underline">{t.terms}</a>
          <a href={status.privacy_url || 'https://www.mid2bid.com/privacy-policy/'} target="_blank" rel="noopener noreferrer" className="text-primary underline">{t.privacy}</a>
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
        <div className={`flex gap-3 ${isRTL ? 'flex-row-reverse' : ''}`}>
          <Button onClick={accept} disabled={saving} className="flex-1" data-testid="terms-gate-accept">
            {saving ? t.saving : t.accept}
          </Button>
          <Button variant="outline" onClick={() => logout()} disabled={saving} data-testid="terms-gate-signout">
            {t.signOut}
          </Button>
        </div>
      </div>
    </div>
  );
}
